import React, { useContext, useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Notification,
  NotificationContext,
} from "../../contexts/NotificationProvider";
import { WikiNotification } from "../../services/wiki.service";
import { formatDistanceToNow } from "date-fns";
import "./Notification.css";
import logo from "../../assets/images/CMYK.jpeg";

const GridIcon = () => <img width="24" height="24" src={logo} alt="" />;
const SuccessIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="#10B981"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8 0C3.58 0 0 3.58 0 8C0 12.42 3.58 16 8 16C12.42 16 16 12.42 16 8C16 3.58 12.42 0 8 0ZM11.4 6.2L7.6 10L5 7.4L6.2 6.2L7.6 7.6L10.2 5L11.4 6.2Z"
    />
  </svg>
);
const LoadingSpinnerIcon = () => <div className="loading-spinner"></div>;

const WIKI_TYPE_TITLES: Record<string, string> = {
  pageMention:    "Mentioned",
  commentMention: "Mentioned",
  notifyAll:      "Notify All",
  pageEdit:       "Page Edited",
  commented:      "Commented",
};

const wikiDescription = (n: WikiNotification): string => {
  const page = n.wikiPageTitle ? `"${n.wikiPageTitle}"` : "a page";
  const actor = n.actor.name;
  switch (n.type) {
    case "pageMention":    return `${actor} tagged you in ${page}.`;
    case "commentMention": return `${actor} tagged you in a comment on ${page}.`;
    case "notifyAll":      return `${actor} sent you a notification about ${page}.`;
    case "pageEdit":       return `${actor} edited your page ${page}.`;
    case "commented":      return `${actor} commented on your page ${page}.`;
    default:               return `${actor} ${n.message}`;
  }
};

const formatRelativeTime = (dateString?: string): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";

  const now = new Date();
  const seconds = Math.round((now.getTime() - date.getTime()) / 1000);
  const minutes = Math.round(seconds / 60);
  const hours = Math.round(minutes / 60);
  const days = Math.round(hours / 24);

  if (seconds < 60) return `${seconds} sec ago`;
  if (minutes < 60) return `${minutes} min ago`;
  if (hours < 24) return `${hours} hr ago`;
  return `${days} day(s) ago`;
};

const wikiRelativeTime = (iso: string) => {
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return ""; }
};

const groupNotificationsByDate = (notifications: Notification[]) => {
  const groups: { [key: string]: Notification[] } = {
    TODAY: [],
    YESTERDAY: [],
    OLDER: [],
  };
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  const validNotifications = notifications.filter(
    (n) => n.createdAt && !isNaN(new Date(n.createdAt).getTime())
  );

  validNotifications.forEach((n) => {
    const notificationDate = new Date(n.createdAt!);
    if (notificationDate.toDateString() === today.toDateString()) {
      groups.TODAY.push(n);
    } else if (notificationDate.toDateString() === yesterday.toDateString()) {
      groups.YESTERDAY.push(n);
    } else {
      groups.OLDER.push(n);
    }
  });

  return Object.entries(groups)
    .map(([title, data]) => ({ title, data }))
    .filter((group) => group.data.length > 0);
};

const Notifications: React.FC = () => {
  const {
    notifications,
    markOneAsRead,
    badgeCount,
    markAllAsSeen,
    markAllAsRead,
    loadMore,
    hasMore,
    loadingMore,
    // Wiki
    wikiNotifications,
    wikiUnreadCount,
    wikiHasMore,
    wikiLoadingMore,
    loadMoreWikiNotifications,
    markWikiRead,
    markAllWikiRead,
    deleteWikiNotif,
    clearAllWikiNotifs,
  } = useContext(NotificationContext);
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"all" | "unread" | "wiki">("all");
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();
  const modalRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const wikiObserverRef = useRef<IntersectionObserver | null>(null);

  const totalBadge = badgeCount + wikiUnreadCount;

  const lastNotificationRef = useCallback((node: HTMLDivElement) => {
    if (loadingMore) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && isExpanded) {
        loadMore();
      }
    });
    if (node) observerRef.current.observe(node);
  }, [loadingMore, hasMore, isExpanded, loadMore]);

  const wikiSentinelRef = useCallback((node: HTMLDivElement | null) => {
    if (wikiObserverRef.current) wikiObserverRef.current.disconnect();
    if (!node) return;
    wikiObserverRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && wikiHasMore && !wikiLoadingMore) {
        loadMoreWikiNotifications();
      }
    });
    wikiObserverRef.current.observe(node);
  }, [wikiHasMore, wikiLoadingMore, loadMoreWikiNotifications]);

  const sortedNotifications = [...notifications].sort(
    (a, b) =>
      new Date(b.createdAt || 0).getTime() -
      new Date(a.createdAt || 0).getTime()
  );

  const unreadNotifications = sortedNotifications.filter((n) => !n.isRead);

  const rawDisplayList = activeTab === "all" ? sortedNotifications : unreadNotifications;
  const visibleNotifications = isExpanded ? rawDisplayList : rawDisplayList.slice(0, 5);
  const groupedNotifications = groupNotificationsByDate(visibleNotifications);

  const toggleModal = () => {
    if (!isOpen) {
      markAllAsSeen();
    } else {
      setIsExpanded(false);
    }
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setIsExpanded(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleItemClick = (notification: Notification) => {
    if (notification.status === "loading") return;
    if (!notification.isRead) markOneAsRead(notification.id);
    if (notification.type === 'agent' && notification.sessionId) {
      window.dispatchEvent(new CustomEvent('prospo:open-agent-session', {
        detail: { sessionId: notification.sessionId },
      }));
    } else if (notification.meetingId) {
      navigate(`/meetings/${notification.meetingId}`);
    }
    setIsOpen(false);
  };

  const handleWikiClick = (n: WikiNotification) => {
    if (!n.isRead) markWikiRead(n.id);
    navigate(`/notes/${n.wikiPageId}`);
    setIsOpen(false);
  };

  return (
    <div className="notification-wrapper" ref={modalRef}>
      <div className="notification-bell-icon" onClick={toggleModal}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18 16V11C18 7.934 16.365 5.358 13.5 4.473V4C13.5 2.62 12.38 1.5 11 1.5C9.62 1.5 8.5 2.62 8.5 4V4.473C5.635 5.358 4 7.934 4 11V16L2 18H20L18 16ZM11 21.5C12.105 21.5 13 20.605 13 19.5H9C9 20.605 9.895 21.5 11 21.5Z" />
        </svg>
        {totalBadge > 0 && (
          <span className="notification-badge">
            {totalBadge > 99 ? "99+" : totalBadge}
          </span>
        )}
      </div>

      {isOpen && (
        <div className="notifications-modal">
          <div className="modal-header">
            <p></p>
            <h3>Notifications</h3>
            <button className="close-button" onClick={toggleModal}>
              &times;
            </button>
          </div>

          <div className="notification-tabs">
            <button
              className={`tab-button ${activeTab === "all" ? "active" : ""}`}
              onClick={() => setActiveTab("all")}
            >
              All <span className="tab-count">{unreadNotifications.length}</span>
            </button>
            <button
              className={`tab-button ${activeTab === "unread" ? "active" : ""}`}
              onClick={() => setActiveTab("unread")}
            >
              Unread <span className="tab-count">{unreadNotifications.length}</span>
            </button>
            <button
              className={`tab-button ${activeTab === "wiki" ? "active" : ""}`}
              onClick={() => setActiveTab("wiki")}
            >
              Notes <span className="tab-count">{wikiUnreadCount}</span>
            </button>
          </div>

          <div className="modal-content">
            {/* ── Meeting notifications (All / Unread tabs) ── */}
            {activeTab !== "wiki" && (
              <>
                {notifications.some(n => !n.isRead) && (
                  <button className="clear-all-button" onClick={markAllAsRead}>
                    MARK ALL AS READ
                  </button>
                )}

                {groupedNotifications.length > 0 ? (
                  groupedNotifications.map((group) => (
                    <div key={group.title} className="date-group">
                      <div className="date-group-header">
                        <span>{group.title}</span>
                      </div>
                      {group.data.map((item, index) => {
                        const isLast = index === group.data.length - 1 &&
                          group.title === groupedNotifications[groupedNotifications.length - 1].title;

                        return (
                          <div
                            key={item.id}
                            ref={isLast && isExpanded ? lastNotificationRef : null}
                            className={`notification-item ${item.isRead ? "read" : "unread"} ${item.status === "loading" ? "loading" : ""}`}
                            onClick={() => handleItemClick(item)}
                          >
                            <div className="item-icon-wrapper">
                              <GridIcon />
                            </div>
                            <div className="item-details">
                              <div className="item-header">
                                <div className="item-title">
                                  {item.status === "loading" ? <LoadingSpinnerIcon /> : <SuccessIcon />}
                                  <span>{item.title}</span>
                                </div>
                                <span className="item-time">{formatRelativeTime(item.createdAt)}</span>
                              </div>
                              <p className="item-message">{item.message}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))
                ) : (
                  <p className="empty-message">You're all caught up!</p>
                )}

                {!isExpanded && rawDisplayList.length > 5 && (
                  <div style={{ textAlign: "center", padding: "15px" }}>
                    <button
                      onClick={() => setIsExpanded(true)}
                      style={{ background: "none", border: "none", color: "#2563eb", fontWeight: 600, cursor: "pointer", fontSize: "13px" }}
                    >
                      Show More
                    </button>
                  </div>
                )}

                {loadingMore && (
                  <div style={{ padding: "10px", textAlign: "center", color: "#6b7280", fontSize: "12px" }}>
                    Loading more...
                  </div>
                )}
              </>
            )}

            {/* ── Wiki notifications (Wiki tab) ── */}
            {activeTab === "wiki" && (
              <>
                {/* Header actions */}
                <div style={{ display: "flex", gap: 8, padding: "6px 12px" }}>
                  {wikiNotifications.some(n => !n.isRead) && (
                    <button className="clear-all-button" style={{ flex: 1, margin: 0 }} onClick={markAllWikiRead}>
                      MARK ALL AS READ
                    </button>
                  )}
                  {wikiNotifications.length > 0 && (
                    <button
                      onClick={clearAllWikiNotifs}
                      style={{
                        background: "none", border: "1px solid #e5e7eb",
                        borderRadius: 4, padding: "4px 10px",
                        color: "#6b7280", fontSize: 11, cursor: "pointer",
                      }}
                    >
                      Clear all
                    </button>
                  )}
                </div>

                {wikiNotifications.length === 0 ? (
                  <p className="empty-message">You're all caught up!</p>
                ) : (
                  wikiNotifications.map((n) => (
                    <div
                      key={n.id}
                      className={`notification-item ${n.isRead ? "read" : "unread"}`}
                      onClick={() => handleWikiClick(n)}
                    >
                      {/* Actor avatar */}
                      <div className="item-icon-wrapper">
                        <div style={{
                          width: 28, height: 28, borderRadius: "50%",
                          background: "#1976d2", color: "#fff",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 12, fontWeight: 700, flexShrink: 0,
                        }}>
                          {n.actor.name.charAt(0).toUpperCase()}
                        </div>
                      </div>

                      <div className="item-details">
                        {/* Header row: bold type label + timestamp */}
                        <div className="item-header">
                          <div className="item-title">
                            <span style={{ fontWeight: 600 }}>
                              {WIKI_TYPE_TITLES[n.type] ?? n.type}
                            </span>
                          </div>
                          <span className="item-time">{wikiRelativeTime(n.createdAt)}</span>
                        </div>

                        {/* Description */}
                        <p className="item-message">
                          {wikiDescription(n)}
                        </p>

                        {/* Per-item actions */}
                        <div
                          style={{ display: "flex", gap: 6, marginTop: 2 }}
                          onClick={e => e.stopPropagation()}
                        >
                          {!n.isRead && (
                            <button
                              onClick={() => markWikiRead(n.id)}
                              style={{
                                background: "none", border: "1px solid #d1d5db",
                                borderRadius: 3, padding: "1px 6px",
                                fontSize: 10, cursor: "pointer", color: "#374151",
                              }}
                            >
                              ✓ Mark read
                            </button>
                          )}
                          <button
                            onClick={() => deleteWikiNotif(n.id)}
                            style={{
                              background: "none", border: "1px solid #d1d5db",
                              borderRadius: 3, padding: "1px 6px",
                              fontSize: 10, cursor: "pointer", color: "#6b7280",
                            }}
                          >
                            × Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}

                {/* Infinite-scroll sentinel */}
                {wikiHasMore && (
                  <div ref={wikiSentinelRef} style={{ padding: "10px", textAlign: "center", color: "#6b7280", fontSize: "12px" }}>
                    {wikiLoadingMore ? "Loading more..." : ""}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications;
